import { App } from "../../ts/app";
import { Controller } from "../../ts/controller";
import { Tab } from "../../ts/models/tab";
import { Logger } from "../../ts/utils/logger";
import { MinMaxBounds } from "../../ts/utils/min-max-bounds";


export interface TabPageInitProps {
    tab: object;
    favorited: boolean;
    userRating: number | null;
    rateUrl: string;
    addFavoriteUrl: string;
    removeFavoriteUrl: string;
}

export class TabPageController extends Controller {
    private tab!: Tab;
    private favorited: boolean = false;
    private userRating: number | null = null;
    private rateUrl!: string;
    private addFavoriteUrl!: string;
    private removeFavoriteUrl!: string;
    private loadingRating: boolean = false;
    private loadingFavorite: boolean = false;

    private autoScrolling: boolean = false;
    private scrollerIntervalId: NodeJS.Timeout | null = null;
    private scrollerIntervalDelay!: number;
    private scrollerIntervalDelayBounds = new MinMaxBounds(5, 250);
    static scrollerIntervalLocalStorageKey = 'tab-page-scroller-interval';


    constructor() {
        super({
            logLabel: 'TabPageController',
        });
        if (document.readyState === "loading") {
            document.addEventListener("DOMContentLoaded", () => {
                const observer = new MutationObserver(this._onMutation.bind(this));
                observer.observe(document.body, { childList: true, subtree: true });
            });
        } else {
            const observer = new MutationObserver(this._onMutation.bind(this));
            observer.observe(document.body, { childList: true, subtree: true });
        }
    }

    private _onMutation(mutations: MutationRecord[]): void {
        for (const mutation of mutations) {
            for (const node of mutation.addedNodes) {
                if (node instanceof HTMLElement && node.id === "tab-page-wrapper") {
                    this._stopScrolling();
                }
            }
            for (const node of mutation.removedNodes) {
                if (node instanceof HTMLElement && node.id === "tab-page-wrapper") {
                    window.appbarController.allowAppbarPrint();
                    this._stopScrolling();
                }
            }
        }
    }

    public refreshWithTab({
        tab,
        favorited,
        userRating,
        rateUrl,
        addFavoriteUrl,
        removeFavoriteUrl,
    }: TabPageInitProps): void {
        this.tab = Tab.fromObject(tab);
        if (App.loggedIn) {
            this.favorited = favorited;
            this.userRating = userRating;
            this.rateUrl = rateUrl;
            this.addFavoriteUrl = addFavoriteUrl;
            this.removeFavoriteUrl = removeFavoriteUrl;
        }
        window.appbarController.blockAppbarPrint();
        this._initializeScrollInterval();
        this._bindClicks();
        if (!App.mockAds) {
            this._launchStickyBottomAd();
        }
        this._setState();
        Logger.info(this.logLabel, `Refreshed with tab: ${this.tab.name}`);
    }

    private _initializeScrollInterval() {
        const storedIntervalString = localStorage.getItem(
            TabPageController.scrollerIntervalLocalStorageKey
        );
        if (storedIntervalString === null) {
            this.scrollerIntervalDelay = this.scrollerIntervalDelayBounds.middle;
            return;
        }
        try {
            const storedInterval = Number(storedIntervalString);
            if (Number.isFinite(storedInterval)) {
                this.scrollerIntervalDelay = this.scrollerIntervalDelayBounds.clamp(storedInterval);
            }
            else {
                this.scrollerIntervalDelay = this.scrollerIntervalDelayBounds.middle;
            }
        }
        catch (e) {
            this.scrollerIntervalDelay = this.scrollerIntervalDelayBounds.middle;
        }
    }

    private _launchStickyBottomAd() {
        googletag.cmd.push(function () {
            googletag.display('div-gpt-ad-1741135231571-0');
        });
    }

    private _bindClicks(): void {
        if (App.loggedIn) {
            this.bindClick({
                elementId: 'clear-rating-button',
                handler: () => this._rate(null),
            });
            this.bindClick({
                elementId: 'favorite-tab-button',
                handler: this._toggleFavorite.bind(this),
            });
            const ratingButtons = document.querySelectorAll<HTMLButtonElement>(
                '#tab-page-rating-buttons .icon-button');
            if (ratingButtons.length === 0) {
                Logger.warn(this.logLabel, 'No rating buttons found.');
            } else {
                ratingButtons.forEach((button) => {
                    const ratingValue = Number(button.dataset.tabRating);
                    button.addEventListener('click', () => this._rate(ratingValue));
                });
            }
        }

        this.bindClick({
            elementId: 'tab-page-start-scrolling-button',
            handler: () => {
                if (this.autoScrolling) {
                    this._stopScrolling();
                }
                else {
                    this._startScrolling();
                }
            },
        })
        this.bindClick({
            elementId: 'tab-page-floating-scroller-stop-button',
            handler: () => {
                this._stopScrolling();
            },
        })
        this.bindClick({
            elementId: 'tab-page-floating-scroller-speed-down-button',
            handler: () => {
                this._modifyScrollSpeed(2);
            },
        });
        this.bindClick({
            elementId: 'tab-page-floating-scroller-speed-up-button',
            handler: () => {
                this._modifyScrollSpeed(0.5);
            },
        });
    }

    private _setState(): void {
        if (App.loggedIn) {
            this._setRatingState();
            this._setUserRatingPipsState();
            this._setAverageAndNumRatingsState();
            this._setFavoriteState();
            this._setRatingAndFavoriteLoadingStates();
        }
        this._setScrollerControlsState();
    }

    private _setRatingState(): void {
        const notRatedYet = this.getElementByIdOrWarn('not-rated-yet');
        const clearRatingButton = this.getElementByIdOrWarn('clear-rating-button');

        if (notRatedYet && clearRatingButton) {
            const hasUserRating = !!this.userRating;
            notRatedYet.style.display = hasUserRating ? 'none' : 'block';
            clearRatingButton.style.display = hasUserRating ? 'block' : 'none';
        }
    }

    private _setUserRatingPipsState(): void {
        const buttons = document.querySelectorAll<HTMLButtonElement>(
            '#tab-page-rating-buttons .icon-button');

        buttons.forEach((button) => {
            const rating = Number(button.dataset.tabRating);
            if (rating <= (this.userRating || 0)) {
                button.classList.add('filled');
            }
            else {
                button.classList.remove('filled');
            }
        });
    }

    private _setAverageAndNumRatingsState(): void {
        const avgRatingElement = this.getElementByIdOrWarn('tab-average-rating-value');
        const numRatingsElement = this.getElementByIdOrWarn('tab-num-rating-value');

        if (avgRatingElement && numRatingsElement) {
            // Update the average rating display
            avgRatingElement.textContent = this.tab.average_rating
                ? `${this.tab.average_rating.toFixed(1)} avg rating`
                : '';

            // Update the number of ratings display with pluralization
            if (this.tab.num_ratings) {
                const ratingText = this.tab.num_ratings === 1 ? 'rating' : 'ratings';
                numRatingsElement.textContent = `${this.tab.num_ratings} ${ratingText}`;
            } else {
                numRatingsElement.textContent = '';
            }
        }
    }

    private _setFavoriteState(): void {
        const favoriteIcon = this.getElementByIdOrWarn('favorite-icon');
        const favoriteStatusText = this.getElementByIdOrWarn('favorite-status-text');
        const templateFavorite = this.getElementByIdOrWarn('icon-template-favorite-filled');
        const templateFavoriteOutlined = this.getElementByIdOrWarn('icon-template-favorite');
        if (favoriteIcon && templateFavorite && templateFavoriteOutlined) {
            favoriteIcon.innerHTML = '';
            const svg = this.favorited
                ? templateFavorite.cloneNode(true) as HTMLElement
                : templateFavoriteOutlined.cloneNode(true) as HTMLElement;
            svg.style.display = 'inline';
            favoriteIcon.appendChild(svg);
        }
        if (favoriteStatusText) {
            favoriteStatusText.textContent = this.favorited ? 'Favorited!' : 'Favorite';
        }
    }

    private _setRatingAndFavoriteLoadingStates(): void {
        const loadingOverlay = this.getElementByIdOrWarn('rating-loading-overlay');
        const favoriteButton = this.getElementByIdOrWarn<HTMLButtonElement>('favorite-tab-button');
        if (loadingOverlay) {
            loadingOverlay.style.display = this.loadingRating ? 'block' : 'none';
        }
        if (favoriteButton) {
            favoriteButton.disabled = this.loadingFavorite;
        }
    }

    private _setScrollerControlsState(): void {
        const controls = this.getElementByIdOrWarn('tab-page-floating-scroller-controls');
        if (controls) {
            controls.style.display = this.autoScrolling
                ? 'flex'
                : 'none';
        }
    }

    private async _rate(newRating: number | null): Promise<void> {
        if (!App.loggedIn || this.loadingRating) {
            return;
        }

        this.loadingRating = true;
        this._setState();

        const MIN_WAIT_TIME = 300;
        const startTime = performance.now();
        try {
            const body = JSON.stringify({ rating: newRating });
            const response = await fetch(this.rateUrl, {
                method: 'POST',
                credentials: 'same-origin',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: body,
            });

            if (!response.ok) {
                throw new Error('Failed to submit rating');
            }

            const data = await response.json();
            this.userRating = data.user_rating;
            this.tab.average_rating = data.average_rating;
            this.tab.num_ratings = data.num_ratings;

            Logger.info(this.logLabel, `Rating updated: ${JSON.stringify(data)}`);
        } catch (error) {
            Logger.error(this.logLabel, `Error rating tab: ${error}`);
        } finally {
            const elapsedTime = performance.now() - startTime;
            if (elapsedTime < MIN_WAIT_TIME) {
                await new Promise((resolve) => setTimeout(resolve, MIN_WAIT_TIME - elapsedTime));
            }
            this.loadingRating = false;
            this._setState();
        }
    }

    private async _toggleFavorite(): Promise<void> {
        if (!App.loggedIn || this.loadingFavorite) {
            return;
        }

        this.loadingFavorite = true;
        this._setState();

        const MIN_WAIT_TIME = 300;
        const startTime = performance.now();
        try {
            const url = this.favorited ? this.removeFavoriteUrl : this.addFavoriteUrl;
            const response = await fetch(url, {
                method: 'POST',
                credentials: 'same-origin',
                headers: {
                    'Content-Type': 'application/json',
                },
            });

            if (!response.ok) throw new Error('Failed to toggle favorite');

            this.favorited = !this.favorited;

            Logger.info(this.logLabel, `Favorite status updated: ${this.favorited}`);
        } catch (error) {
            Logger.error(this.logLabel, `Error toggling favorite: ${error}`);
        } finally {
            const elapsedTime = performance.now() - startTime;
            if (elapsedTime < MIN_WAIT_TIME) {
                await new Promise((resolve) => setTimeout(resolve, MIN_WAIT_TIME - elapsedTime));
            }
            this.loadingFavorite = false;
            this._setState();
        }
    }

    private _modifyScrollSpeed(multiplier: number): void {
        this.scrollerIntervalDelay = this.scrollerIntervalDelayBounds.clamp(
            this.scrollerIntervalDelay * multiplier);
        clearInterval(this.scrollerIntervalId);
        this._setScrollInterval();
        localStorage.setItem(
            TabPageController.scrollerIntervalLocalStorageKey,
            this.scrollerIntervalDelay.toString()
        );
    }

    private _startScrolling(): void {
        if (this.autoScrolling) {
            return;
        }
        this.autoScrolling = true;
        this._setState();
        this._setScrollInterval();
    }

    private _setScrollInterval(): void {
        this.scrollerIntervalId = setInterval(
            () => {
                window.scrollTo({
                    left: 0,
                    top: window.scrollY + 1,
                });
            },
            this.scrollerIntervalDelay,
        );
    }

    private _stopScrolling(): void {
        this.autoScrolling = false;
        this._setState();
        clearInterval(this.scrollerIntervalId);
    }
}
